import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import WistiaPlayer from '@zenbusiness/zui/WistiaPlayer';

import { Information } from './components/Infomation';
import { SocialProof } from './components/SocialProof';
import * as Styled from './InformationSection.styles';
import { useProductLandingContext } from '../../contexts/useProductLanding';

function InformationBlock({
  title, items, hasIcon, hasNumber, hasDivider, alt, image, hasMobileImage, video, disclaimers = []
}) {
  const { formatMessage } = useIntl();

  const mediaContent = image
    ? (
      <SharedAsset
        alt={alt ? formatMessage({ id: alt }) : alt}
        src={image}
      />
    ) : (
      <WistiaPlayer
        clickstreamProps={{
          videoName: 'registered_agent_video'
        }}
        videoId={video}
      />
    );

  return (
    <Stack
      gap={hasMobileImage ? 4 : 7}
      sx={{ flexDirection: { xs: hasMobileImage ? 'column-reverse' : 'row', lg: 'row' } }}
    >
      <Stack
        gap={3}
        sx={{
          maxWidth: { xs: '100%', lg: '50%' },
          flex: 1
        }}
      >
        <Stack gap={1}>
          <Heading component="h2" size="xl" wrap="wrap">
            <FormattedMessage id={title} />
          </Heading>
        </Stack>
        <Information
          items={items}
          hasIcon={hasIcon}
          hasNumber={hasNumber}
          hasDivider={hasDivider}
          disclaimers={disclaimers}
        />
      </Stack>
      <Styled.InfoImage sx={{
        display: { xs: hasMobileImage ? 'block' : 'none', lg: 'block' }
      }}
      >
        {mediaContent}
      </Styled.InfoImage>
    </Stack>
  );
}

InformationBlock.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string
    })
  ),
  hasIcon: PropTypes.bool,
  hasNumber: PropTypes.bool,
  hasDivider: PropTypes.bool,
  hasMobileImage: PropTypes.bool,
  image: PropTypes.string,
  alt: PropTypes.string,
  video: PropTypes.string,
  disclaimers: PropTypes.arrayOf(PropTypes.string)
};

function DisclaimersBlock({ disclaimers = [] }) {
  return (
    <Stack gap={1} paddingTop={2}>
      {disclaimers?.map((disclaimer) => (
        <Text key={disclaimer} size="sm" color="secondary">
          <FormattedMessage id={disclaimer} />
        </Text>
      ))}
    </Stack>
  );
}

DisclaimersBlock.propTypes = {
  disclaimers: PropTypes.arrayOf(PropTypes.string)
};

function InformationSection() {
  const { productConfig } = useProductLandingContext();
  const {
    highlight, howItWorks, whatsIncluded, socialProof, disclaimers = []
  } = productConfig;

  return (
    <>
      {howItWorks && whatsIncluded && !highlight && <Divider />}
      {howItWorks && <InformationBlock {...howItWorks} />}
      {howItWorks && (whatsIncluded || socialProof) && <Divider />}
      {socialProof && <SocialProof {...socialProof} />}
      {socialProof && whatsIncluded && <Divider />}
      {whatsIncluded && <InformationBlock {...whatsIncluded} />}
      {disclaimers.length > 0 && <DisclaimersBlock disclaimers={disclaimers} />}
    </>
  );
}

export default InformationSection;
